import { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { UserPreferences } from '../services/userService';

/**
 * Hook that applies user preferences (theme, language) on authentication.
 * Should be used in a component that renders after authentication.
 *
 * Note: setTheme from next-themes and i18n from react-i18next are stored in
 * refs to avoid triggering re-runs of the preferences effect when those
 * library internals change reference — the effect should only re-run when
 * the user's actual preference data changes.
 */
export const useUserPreferences = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { setTheme } = useTheme();
  const { i18n } = useTranslation();

  // Keep refs up-to-date so the effect below always calls the latest version
  // without listing them as reactive dependencies.
  const setThemeRef = useRef(setTheme);
  const i18nRef = useRef(i18n);
  useEffect(() => { setThemeRef.current = setTheme; });
  useEffect(() => { i18nRef.current = i18n; });

  useEffect(() => {
    if (isAuthenticated && user?.preferences) {
      const prefs = user.preferences as UserPreferences;

      if (prefs.theme && ['light', 'dark', 'system'].includes(prefs.theme)) {
        setThemeRef.current(prefs.theme);
      }

      if (prefs.language && ['fr', 'en'].includes(prefs.language)) {
        if (i18nRef.current.language !== prefs.language) {
          void i18nRef.current.changeLanguage(prefs.language);
        }
      }
    }
  }, [isAuthenticated, user?.preferences]);
};

export default useUserPreferences;
